/**
 * Monte Carlo simulation engine for the ROI calculator.
 * Runs entirely in the browser — no backend required.
 */

const NUM_SIMULATIONS = 10_000;

export interface SimulationParams {
  registration: number; // 0-1
  diy: number; // 0-1
  optIn: number; // 0-1 (marketing consent rate)
  conversion: number; // 0-1
  units: number;
  upsell: number;
  share: number; // 0-1
}

export interface SimulationResult {
  percentiles: Record<string, number>; // '5','25','50','75','95'
  monthlyPercentiles: Record<string, number>;
  meanAnnual: number;
  histCounts: number[];
  histEdges: number[];
  probAbove5k: number;
  probAbove10k: number;
  probAbove25k: number;
  probAbove50k: number;
  medianDiyCustomers: number;
  medianRegistered: number;
  medianOptedIn: number;
  medianPurchasers: number;
  medianUpsellRevenue: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function normalRandom(): number {
  // Box-Muller transform using Math.random()
  let u1 = Math.random();
  // Avoid log(0)
  while (u1 === 0) u1 = Math.random();
  const u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

function samplePct(mean: number, std: number, n: number): Float64Array {
  const result = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    result[i] = clamp(mean + normalRandom() * std, 0.01, 1.0);
  }
  return result;
}

function samplePositive(mean: number, cv: number, n: number): Float64Array {
  const std = mean * cv;
  const result = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    result[i] = clamp(mean + normalRandom() * std, mean * 0.1, mean * 3.0);
  }
  return result;
}

function percentile(sorted: Float64Array, p: number): number {
  const idx = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (idx - lower);
}

function median(sorted: Float64Array): number {
  return percentile(sorted, 50);
}

function histogram(data: Float64Array, bins: number): { counts: number[]; edges: number[] } {
  const min = data[0];
  const max = data[data.length - 1];
  const range = max - min || 1;
  const binWidth = range / bins;
  const counts = new Array(bins).fill(0);
  const edges = new Array(bins + 1);
  for (let i = 0; i <= bins; i++) edges[i] = min + i * binWidth;
  for (let i = 0; i < data.length; i++) {
    const bin = Math.min(Math.floor((data[i] - min) / binWidth), bins - 1);
    counts[bin]++;
  }
  return { counts, edges };
}

export function runMonteCarlo(params: SimulationParams): SimulationResult {
  const n = NUM_SIMULATIONS;

  const registration = samplePct(params.registration, 0.10, n);
  const diy = samplePct(params.diy, 0.10, n);
  const optIn = samplePct(params.optIn, 0.10, n);
  const conversion = samplePct(params.conversion, params.conversion * 0.4, n);
  const units = samplePositive(params.units, 0.15, n);
  const upsell = samplePositive(params.upsell, 0.25, n);

  const annualRevenue = new Float64Array(n);
  const diyCustomers = new Float64Array(n);
  const registered = new Float64Array(n);
  const optedIn = new Float64Array(n);
  const purchasers = new Float64Array(n);
  const totalUpsell = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    diyCustomers[i] = units[i] * diy[i];
    registered[i] = diyCustomers[i] * registration[i];
    optedIn[i] = registered[i] * optIn[i];
    purchasers[i] = optedIn[i] * conversion[i];
    totalUpsell[i] = purchasers[i] * upsell[i];
    annualRevenue[i] = totalUpsell[i] * params.share;
  }

  // Sort for percentiles
  const sortedRevenue = new Float64Array(annualRevenue).sort();
  const sortedMonthly = new Float64Array(n);
  for (let i = 0; i < n; i++) sortedMonthly[i] = sortedRevenue[i] / 12;

  const sortedDiy = new Float64Array(diyCustomers).sort();
  const sortedReg = new Float64Array(registered).sort();
  const sortedOptIn = new Float64Array(optedIn).sort();
  const sortedPurch = new Float64Array(purchasers).sort();
  const sortedUpsell = new Float64Array(totalUpsell).sort();

  const pcts = [5, 25, 50, 75, 95];
  const annualPcts: Record<string, number> = {};
  const monthlyPcts: Record<string, number> = {};
  for (const p of pcts) {
    annualPcts[String(p)] = percentile(sortedRevenue, p);
    monthlyPcts[String(p)] = percentile(sortedMonthly, p);
  }

  // Probability thresholds
  let above5k = 0, above10k = 0, above25k = 0, above50k = 0;
  for (let i = 0; i < n; i++) {
    if (annualRevenue[i] > 5000) above5k++;
    if (annualRevenue[i] > 10000) above10k++;
    if (annualRevenue[i] > 25000) above25k++;
    if (annualRevenue[i] > 50000) above50k++;
  }

  const hist = histogram(sortedRevenue, 40);

  return {
    percentiles: annualPcts,
    monthlyPercentiles: monthlyPcts,
    meanAnnual: sortedRevenue.reduce((a, b) => a + b, 0) / n,
    histCounts: hist.counts,
    histEdges: hist.edges,
    probAbove5k: (above5k / n) * 100,
    probAbove10k: (above10k / n) * 100,
    probAbove25k: (above25k / n) * 100,
    probAbove50k: (above50k / n) * 100,
    medianDiyCustomers: median(sortedDiy),
    medianRegistered: median(sortedReg),
    medianOptedIn: median(sortedOptIn),
    medianPurchasers: median(sortedPurch),
    medianUpsellRevenue: median(sortedUpsell),
  };
}
